import { cva, type VariantProps } from 'class-variance-authority';

import { DARK_CONTROL_TOGGLED } from '../../styles/elevation';

/**
 * Stepper visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, "Progress indicators" page, Stepper frames:
 * "Steps element" (`2812:50376`), "Steps+label" (`2812:50180`), composed
 * "Stepper" (`2812:50467`). Those are the dark-mode frames; the palette
 * tokens are identical in light (verified via get_variable_defs — the dark
 * values are the exact mirror of light), so no `dark:` classes here: the
 * auto-inverting CSS-var palette handles the theme.
 *
 * Axes:
 *   size:  small (24px) | medium (32px) | large (44px) — Figma's "Size".
 *   type:  numbered | icon | indicator — what renders inside the circle.
 *          Only affects the CVA where Figma differs (indicator's Active ring).
 *   state: idle | active | completed | error | disabled — Figma's "State"
 *          (Idle · Active · Success · Error · Deactivated/Disabled). Disabled
 *          uses the composed "Steps+label / Disabled" treatment: idle colours
 *          + `opacity-50` on the whole row (applied in Stepper.tsx), NOT the
 *          grey-filled standalone "Deactivated" element.
 *
 * Circle spec per state (from the Steps element frame):
 *   idle/disabled → bg secondary-50, 1px border secondary-100, fg secondary-600
 *   active        → bg secondary-700, 2px border secondary-200, fg secondary-100
 *                   (indicator: 1px border secondary-600 instead)
 *   completed     → bg green-50, 1.5px border green-500 (2px at large), fg green-500
 *   error         → bg red-50,   1.5px border red-500   (2px at large), fg red-500
 *
 * The `text-*` colour sits on the circle so web icons inherit it via CSS
 * `currentColor`; native icons resolve an explicit hex (Stepper.icons.ts) and
 * native number text re-applies its own colour (stepperNumberVariants) since
 * RN Text doesn't inherit from View.
 */
export const stepperCircleVariants = cva(
  'shrink-0 items-center justify-center overflow-hidden rounded-full border-solid',
  {
    variants: {
      size: {
        small: 'size-6',
        medium: 'size-8',
        large: 'size-11',
      },
      state: {
        idle: `border bg-secondary-50 border-secondary-100 text-secondary-600 ${DARK_CONTROL_TOGGLED}`,
        active: 'border-2 bg-secondary-700 border-secondary-200 text-secondary-100',
        completed: 'border-[1.5px] bg-green-50 border-green-500 text-green-500',
        error: 'border-[1.5px] bg-red-50 border-red-500 text-red-500',
        disabled: `border bg-secondary-50 border-secondary-100 text-secondary-600 ${DARK_CONTROL_TOGGLED}`,
      },
      type: { numbered: '', icon: '', indicator: '' },
    },
    compoundVariants: [
      // Indicator's Active ring is thinner and tone-on-tone (1px secondary-600).
      { type: 'indicator', state: 'active', class: 'border border-secondary-600' },
      // Large completed/error rings step up to 2px.
      { size: 'large', state: 'completed', class: 'border-2' },
      { size: 'large', state: 'error', class: 'border-2' },
    ],
    defaultVariants: { size: 'small', state: 'idle', type: 'numbered' },
  },
);

/**
 * The number inside a `numbered` circle — Mona Sans Medium, B8 10 (small) /
 * B3 12 (medium) / B1 16 (large). `!leading-[normal]` (not the Figma fixed
 * line-heights) so RN uses font metrics and avoids ascender clipping — the
 * number is centred by the circle's flexbox anyway.
 */
export const stepperNumberVariants = cva(
  'text-center font-medium !leading-[normal] native:font-mona-medium',
  {
    variants: {
      size: {
        small: 'text-[10px]',
        medium: 'text-xs',
        large: 'text-base',
      },
      state: {
        idle: 'text-secondary-600',
        active: 'text-secondary-100',
        completed: 'text-green-500',
        error: 'text-red-500',
        disabled: 'text-secondary-600',
      },
    },
    defaultVariants: { size: 'small', state: 'idle' },
  },
);

/**
 * The dot inside an `indicator` circle — 6 / 8 / 10 px per size; colour
 * mirrors the state foreground.
 */
export const stepperDotVariants = cva('shrink-0 rounded-full', {
  variants: {
    size: {
      small: 'size-1.5',
      medium: 'size-2',
      large: 'size-2.5',
    },
    state: {
      idle: 'bg-secondary-600',
      active: 'bg-secondary-100',
      completed: 'bg-green-500',
      error: 'bg-red-500',
      disabled: 'bg-secondary-600',
    },
  },
  defaultVariants: { size: 'small', state: 'idle' },
});

/**
 * One step row (circle + text block) — the circle↔label gap ramps 8/10/12 px
 * with size (from the Steps+label per-size symbols).
 */
export const stepperStepRowVariants = cva('flex-row items-center', {
  variants: {
    size: {
      small: 'gap-2',
      medium: 'gap-2.5',
      large: 'gap-3',
    },
    disabled: { true: 'opacity-50', false: '' },
  },
  defaultVariants: { size: 'small', disabled: false },
});

/**
 * Step label + description typography — constant across sizes and states
 * (L3 Bold 12/16 in secondary-500 · B2 Medium 14/22 in secondary-800, which
 * invert cleanly in dark mode via the palette).
 */
export const STEPPER_LABEL_CLASS =
  'text-xs font-bold leading-4 native:font-mona-bold text-fg-subtle';
export const STEPPER_DESCRIPTION_CLASS =
  'text-sm font-medium leading-[22px] native:font-mona-medium text-secondary-800';

/** Connector line between steps — 1px, secondary-200, both orientations. */
export const STEPPER_CONNECTOR_CLASS = 'bg-secondary-200';

export type StepperVariantProps = VariantProps<typeof stepperCircleVariants>;
